import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, Mail } from "../components/icons";
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserAgreementsModal } from "../components/UserAgreementsModal";
import { useAuth } from "../contexts/AuthContext";
import { checkUserRole } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { useDomainBranding } from "../hooks/useBranding";

const BG_URL =
  "https://images.pexels.com/photos/10195041/pexels-photo-10195041.jpeg?auto=compress&cs=tinysrgb&h=1280&w=1920";

export function LoginPageNLP() {
  const navigate = useNavigate();
  const { user, checkAgreements } = useAuth();
  const { branding } = useDomainBranding();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showAgreements, setShowAgreements] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [credentials, setCredentials] = useState({ email: "", password: "" });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });
      if (signInError) throw signInError;
      if (!data.user) throw new Error("Login failed");
      const role = await checkUserRole(data.user.id);
      if (!role) {
        await supabase.auth.signOut();
        throw new Error("User role not found");
      }
    } catch (err: any) {
      if (err.message?.includes("Invalid login credentials")) {
        setError("Invalid email or password");
      } else if (err.message?.includes("Email not confirmed")) {
        setError("Please confirm your email address");
      } else {
        setError(err.message || "Failed to log in");
      }
      await supabase.auth.signOut();
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    setResetSuccess(false);
    try {
      const { error: fnError } = await supabase.functions.invoke("reset-password", {
        body: { email: resetEmail, locale: "en" },
      });
      if (fnError) throw fnError;
      setResetSuccess(true);
    } catch (err: any) {
      setError(err.message || "Failed to send password reset email");
    } finally {
      setIsLoading(false);
    }
  };

  const redirectByRole = async (uid: string) => {
    const role = await checkUserRole(uid);
    switch (role) {
      case "admin": navigate("/admin"); break;
      case "partner": navigate("/partner"); break;
      case "coach": navigate("/coach"); break;
      case "respondent": navigate("/dashboard"); break;
    }
  };

  const handleAgreementsSuccess = async () => {
    if (pendingUserId) await redirectByRole(pendingUserId);
  };

  const handleUserLoggedIn = async (uid: string) => {
    try {
      setIsLoading(true);
      setPendingUserId(uid);
      const hasAgreements = await checkAgreements(uid);
      if (!hasAgreements) {
        setShowAgreements(true);
        setIsLoading(false);
        return;
      }
      await redirectByRole(uid);
    } catch (err) {
      console.error("Error handling user logged in:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        supabase.auth.signOut().then(() => setIsReady(true));
      } else {
        setIsReady(true);
      }
    });
  }, []);

  useEffect(() => {
    if (isReady && user?.id) handleUserLoggedIn(user.id);
  }, [user, isReady]);

  return (
    <div className="min-h-screen flex">
      {/* Left panel — nature background */}
      <div
        className="hidden lg:flex lg:w-[55%] relative flex-col justify-end p-12"
        style={{
          backgroundImage: `url(${BG_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1F3D2E]/80 via-[#1F3D2E]/40 to-transparent" />

        <div className="relative z-10">
          <p
            className="text-[#E7DCC2]/70 text-xs tracking-[0.25em] uppercase mb-4"
            style={{ fontFamily: "Georgia, serif" }}
          >
            {branding.brandName}
          </p>
          <h2
            className="text-white text-4xl font-bold leading-tight mb-4"
            style={{ fontFamily: "Georgia, serif" }}
          >
            Your Operating System<br />
            <span style={{ color: "#A69884" }}>For Change</span>
          </h2>
          <p className="text-[#E7DCC2]/80 text-base max-w-sm leading-relaxed">
            All the intelligence, tools and support you need to understand your clients, guide real change and grow your coaching business.
          </p>

          <div className="mt-10 flex gap-6">
            {[
              { label: "Built for Coaches" },
              { label: "AI-Powered Insights" },
              { label: "Secure & Private" },
            ].map((f) => (
              <div key={f.label} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#A69884]" />
                <span className="text-[#E7DCC2]/70 text-xs tracking-wide">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel — login card */}
      <div
        className="flex-1 flex flex-col items-center justify-center px-6 py-12 relative"
        style={{ backgroundColor: "#F5F0E8" }}
      >
        {/* Subtle background texture */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: "radial-gradient(circle at 2px 2px, #1F3D2E 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="relative w-full max-w-sm"
        >
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src={branding.logoUrl}
              alt={branding.brandName}
              className="h-16 w-auto object-contain"
            />
          </div>

          {/* Card */}
          <div
            className="bg-white rounded-2xl shadow-xl border p-8"
            style={{ borderColor: "#E7DCC2" }}
          >
            <div className="mb-6">
              <h1
                className="text-2xl font-bold mb-1"
                style={{ color: "#1F3D2E", fontFamily: "Georgia, serif" }}
              >
                {showForgotPassword ? "Reset Password" : "Welcome back"}
              </h1>
              <p className="text-sm" style={{ color: "#A69884" }}>
                {showForgotPassword
                  ? "Enter your email to receive reset instructions"
                  : `Sign in to access your ${branding.brandName} dashboard.`}
              </p>
            </div>

            <AnimatePresence mode="wait">
              {error && (
                <motion.div
                  key="error"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="flex items-center gap-2 bg-red-50 text-red-700 px-4 py-3 rounded-xl mb-5 text-sm"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </motion.div>
              )}
              {resetSuccess && (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl mb-5 text-sm"
                  style={{ backgroundColor: "#F0F4EE", color: "#1F3D2E" }}
                >
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  Check your email for your new login details.
                </motion.div>
              )}
            </AnimatePresence>

            {showForgotPassword ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <InputField
                  id="resetEmail"
                  type="email"
                  placeholder="Email address"
                  value={resetEmail}
                  onChange={(v) => setResetEmail(v)}
                  disabled={isLoading}
                  icon={<MailIcon />}
                />

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
                  style={{ backgroundColor: "#1F3D2E" }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#2a5040")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1F3D2E")}
                >
                  {isLoading ? "Sending..." : "Send Reset Link"}
                </button>

                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="w-full text-center text-sm transition-colors"
                  style={{ color: "#6A7F64" }}
                >
                  Back to Sign In
                </button>
              </form>
            ) : (
              <form onSubmit={handleLogin} className="space-y-4">
                <InputField
                  id="email"
                  type="email"
                  placeholder="Email address"
                  value={credentials.email}
                  onChange={(v) => setCredentials((p) => ({ ...p, email: v }))}
                  disabled={isLoading}
                  icon={<UserIcon />}
                />

                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#A69884" }}>
                    <LockIcon />
                  </span>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={credentials.password}
                    onChange={(e) => setCredentials((p) => ({ ...p, password: e.target.value }))}
                    required
                    disabled={isLoading}
                    className="w-full pl-9 pr-10 py-3 rounded-xl text-sm border transition-all outline-none"
                    style={{
                      borderColor: "#E7DCC2",
                      backgroundColor: "#FDFAF5",
                      color: "#2B2B2B",
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = "#1F3D2E";
                      e.currentTarget.style.boxShadow = "0 0 0 3px rgba(31,61,46,0.1)";
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = "#E7DCC2";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: "#A69884" }}
                    tabIndex={-1}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>

                <div className="flex justify-end -mt-1">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-xs font-medium transition-colors hover:underline"
                    style={{ color: "#6A7F64" }}
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50 mt-2"
                  style={{ backgroundColor: "#1F3D2E" }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#2a5040")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1F3D2E")}
                >
                  {isLoading ? "Signing in..." : "Sign In"}
                </button>
              </form>
            )}

            <div className="mt-6 pt-5 border-t text-center" style={{ borderColor: "#E7DCC2" }}>
              <p className="text-xs" style={{ color: "#A69884" }}>
                New here?{" "}
                <Link
                  to="/register"
                  className="font-semibold transition-colors"
                  style={{ color: "#1F3D2E" }}
                >
                  Create an account
                </Link>
              </p>
            </div>
          </div>

          {/* Bottom brand tagline */}
          <p
            className="text-center text-xs mt-6 tracking-widest uppercase"
            style={{ color: "#A69884" }}
          >
            Your Operating System For Change
          </p>
        </motion.div>
      </div>

      <UserAgreementsModal
        isOpen={showAgreements}
        onClose={() => setShowAgreements(false)}
        userId={pendingUserId}
        onSuccess={handleAgreementsSuccess}
      />
    </div>
  );
}

/* ── Small reusable pieces ── */

function InputField({
  id, type, placeholder, value, onChange, disabled, icon,
}: {
  id: string;
  type: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  icon: React.ReactNode;
}) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#A69884" }}>
        {icon}
      </span>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        disabled={disabled}
        className="w-full pl-9 pr-4 py-3 rounded-xl text-sm border transition-all outline-none"
        style={{
          borderColor: "#E7DCC2",
          backgroundColor: "#FDFAF5",
          color: "#2B2B2B",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = "#1F3D2E";
          e.currentTarget.style.boxShadow = "0 0 0 3px rgba(31,61,46,0.1)";
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = "#E7DCC2";
          e.currentTarget.style.boxShadow = "none";
        }}
      />
    </div>
  );
}

function UserIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}
